import { useEffect, useState, useRef, useCallback } from "react";
import { Container, TextField, Button, Grid, Typography, ButtonGroup, CardHeader, MenuItem, IconButton, Tooltip, Chip } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import axios from "axios";
import moment from "moment";
import MaterialTable from "material-table";
import CreateUtilityLicenseV2Form from "../components/forms/utilityLicenseV2/Create";
import EditUtilityLicenseV2Form from "../components/forms/utilityLicenseV2/Edit";
import ViewUtilityLicenseV2Form from "../components/forms/utilityLicenseV2/View";

const statusOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspended" },
    { value: "revoked", label: "Revoked" },
];

const expiringOptions = [
    { value: "all", label: "All" },
    { value: "expired", label: "Expired" },
    { value: "expiring_30_days", label: "Expiring in 30 Days" },
    { value: "expiring_90_days", label: "Expiring in 90 Days" },
];

const hasActivationOptions = [
    { value: "all", label: "All" },
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
];

const defaultFilter = {
    license_code: "",
    product_code: "",
    customer_code: "",
    customer_name: "",
    status: "all",
    expiring: "all",
    has_activation: "all",
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }

    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "";
};

const getStatusChipConfig = (status) => {
    if (!status) {
        return { label: "-", color: "default", textColor: "inherit" };
    }

    const normalizedStatus = String(status).toLowerCase();

    if (normalizedStatus === "active") {
        return { label: "Active", color: "success", textColor: "white" };
    }

    if (normalizedStatus === "suspended") {
        return { label: "Suspended", color: "warning", textColor: "white" };
    }

    if (normalizedStatus === "revoked") {
        return { label: "Revoked", color: "error", textColor: "white" };
    }

    if (normalizedStatus === "released") {
        return { label: "Released", color: "info", textColor: "white" };
    }

    return { label: normalizedStatus, color: "default", textColor: "inherit" };
};

export const UtilityLicenseV2Page = () => {
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const classes = CustomStyles;

    const licenseCodeInputRef = useRef(null);
    const productCodeInputRef = useRef(null);
    const customerCodeInputRef = useRef(null);
    const customerNameInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [filter, setFilter] = useState({ ...defaultFilter });
    const [open, setOpen] = useState({
        create: false,
        edit: false,
        view: false,
    });
    const [selectedRow, setSelectedRow] = useState(null);

    const handleOpen = (type, row) => {
        setOpen((prevState) => ({ ...prevState, [type]: true }));
        setSelectedRow(row);
    };

    const handleClose = (type) => {
        setOpen((prevState) => ({ ...prevState, [type]: false }));
        setSelectedRow(null);
    };

    const handleResetFilter = () => {
        setPage(0);
        setFilter({ ...defaultFilter });
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter({ ...filter, [event.target.name]: event.target.value });
    };

    const fetchData = useCallback(async () => {
        const preparedFilter = {};

        if (filter.license_code) {
            preparedFilter.license_code = filter.license_code;
        }

        if (filter.product_code) {
            preparedFilter.product_code = filter.product_code;
        }

        if (filter.customer_code) {
            preparedFilter.customer_code = filter.customer_code;
        }

        if (filter.customer_name) {
            preparedFilter.customer_name = filter.customer_name;
        }

        if (filter.status !== "all") {
            preparedFilter.status = filter.status;
        }

        if (filter.expiring !== "all") {
            preparedFilter.expiring = filter.expiring;
        }

        if (filter.has_activation !== "all") {
            preparedFilter.has_activation = filter.has_activation;
        }

        try {
            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v2/utility-license", {
                params: {
                    page: page + 1,
                    perPage,
                    filter: JSON.stringify(preparedFilter),
                    sort: JSON.stringify({ field: "id", order: "desc" }),
                },
            });

            setData(response.data.data.licenses || []);
            setCount(Number(response.data.data.total) || 0);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [filter, logout, page, perPage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = [
        {
            title: "License Code",
            field: "remarks",
            cellStyle: { width: 190, maxWidth: 190 },
            headerStyle: { width: 190, maxWidth: 190 },
        },
        { title: "Product Code", field: "product_code" },
        { title: "Customer Code", field: "customer_code" },
        { title: "Customer Name", field: "customer_name" },
        {
            title: "Main Status",
            field: "status",
            render: (rowData) => {
                const statusConfig = getStatusChipConfig(rowData.status);
                return <Chip label={statusConfig.label} color={statusConfig.color} size="small" style={{ color: statusConfig.textColor }} />;
            },
        },
        {
            title: "Device Status",
            field: "device_status",
            render: (rowData) => {
                const statusConfig = getStatusChipConfig(rowData.device_status);
                return <Chip label={statusConfig.label} color={statusConfig.color} size="small" style={{ color: statusConfig.textColor }} />;
            },
        },
        {
            title: "Has Activation",
            field: "activation_count",
            render: (rowData) => (Number(rowData.activation_count) > 0 ? "Yes" : "No"),
        },
        {
            title: "Expires At",
            field: "expires_at",
            render: (rowData) => formatDate(rowData.expires_at),
        },
        {
            title: "Actions",
            field: "actions",
            align: "center",
            cellStyle: { width: 100, maxWidth: 100 },
            headerStyle: { width: 100, maxWidth: 100 },
            render: (rowData) => (
                <>
                    <Tooltip title="View Utility License Information">
                        <IconButton color="primary" aria-label="view utility license information" component="label" style={{ padding: 0 }} onClick={() => handleOpen("view", rowData)}>
                            <Icons.Preview />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Utility License Information">
                        <IconButton color="primary" aria-label="edit utility license information" component="label" style={{ padding: 0, marginLeft: 8 }} onClick={() => handleOpen("edit", rowData)}>
                            <Icons.Edit />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                {identity.user.role_id === 1 || identity.user.role_id === 2 ? (
                    <MaterialTable
                        title="Utility License v2 List"
                        icons={Icons}
                        columns={columns}
                        data={data}
                        page={page}
                        totalCount={count}
                        style={{ padding: "24px" }}
                        options={{
                            pageSize: perPage,
                            pageSizeOptions: [10, 20, 30, 50],
                            search: false,
                            filtering: false,
                            sorting: false,
                            actionsColumnIndex: -1,
                            headerStyle: {
                                fontWeight: "bold",
                                backgroundColor: "#f1f1f1",
                                color: "#212122",
                            },
                            maxBodyHeight: "600px",
                            paginationType: "stepped",
                            padding: "dense",
                            draggable: false,
                        }}
                        localization={{
                            pagination: {
                                labelRowsPerPage: "",
                                labelDisplayedRows: "{from}-{to} of {count}",
                                labelRowsSelect: "rows",
                            },
                            toolbar: {
                                nRowsSelected: "{0} row(s) selected",
                            },
                            header: {
                                actions: "Actions",
                            },
                            body: {
                                emptyDataSourceMessage: "No data to display",
                            },
                        }}
                        onChangePage={(nextPage) => setPage(nextPage)}
                        onChangeRowsPerPage={(nextPerPage) => setPerPage(nextPerPage)}
                        components={{
                            Toolbar: () => (
                                <>
                                    <div>
                                        <Grid container spacing={2} sx={{ px: 0.1, pb: 3.5 }}>
                                            <Grid item xs={12} sm={3}>
                                                <CardHeader
                                                    sx={{ p: 0 }}
                                                    title={
                                                        <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                            Utility License v2 List
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={9} style={{ textAlign: "right" }}>
                                                <ButtonGroup>
                                                    <Button
                                                        onClick={() => handleOpen("create", null)}
                                                        variant="outlined"
                                                        startIcon={<Icons.AddIcon fontWeight="bold" />}
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 800,
                                                            paddingTop: 0.5,
                                                            paddingBottom: 0.5,
                                                        }}
                                                    >
                                                        Create
                                                    </Button>
                                                </ButtonGroup>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <form>
                                        <Grid container spacing={2} sx={{ paddingBottom: 1.5 }}>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="License Code"
                                                    name="license_code"
                                                    variant="outlined"
                                                    autoFocus={licenseCodeInputRef.current === document.activeElement}
                                                    value={filter.license_code}
                                                    onChange={handleFilterChange}
                                                    inputRef={licenseCodeInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="Product Code"
                                                    name="product_code"
                                                    variant="outlined"
                                                    autoFocus={productCodeInputRef.current === document.activeElement}
                                                    value={filter.product_code}
                                                    onChange={handleFilterChange}
                                                    inputRef={productCodeInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="Customer Code"
                                                    name="customer_code"
                                                    variant="outlined"
                                                    autoFocus={customerCodeInputRef.current === document.activeElement}
                                                    value={filter.customer_code}
                                                    onChange={handleFilterChange}
                                                    inputRef={customerCodeInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    label="Customer Name"
                                                    name="customer_name"
                                                    variant="outlined"
                                                    autoFocus={customerNameInputRef.current === document.activeElement}
                                                    value={filter.customer_name}
                                                    onChange={handleFilterChange}
                                                    inputRef={customerNameInputRef}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Main Status"
                                                    variant="outlined"
                                                    name="status"
                                                    value={filter.status}
                                                    onChange={handleFilterChange}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                >
                                                    {statusOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Expiring"
                                                    variant="outlined"
                                                    name="expiring"
                                                    value={filter.expiring}
                                                    onChange={handleFilterChange}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                >
                                                    {expiringOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Has Activation"
                                                    variant="outlined"
                                                    name="has_activation"
                                                    value={filter.has_activation}
                                                    onChange={handleFilterChange}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                >
                                                    {hasActivationOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="error"
                                                    sx={{ color: "#ffffff", marginTop: "2px", minWidth: "unset", paddingLeft: 0.5, paddingRight: 0.5 }}
                                                    onClick={handleResetFilter}
                                                >
                                                    Reset
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </>
                            ),
                        }}
                    />
                ) : (
                    <Typography variant="subtitle2">You don't have permission to view utility licenses.</Typography>
                )}
                <CreateUtilityLicenseV2Form open={open.create} handleClose={() => handleClose("create")} fetchData={fetchData} />
                <EditUtilityLicenseV2Form open={open.edit} handleClose={() => handleClose("edit")} rowData={selectedRow} fetchData={fetchData} />
                <ViewUtilityLicenseV2Form open={open.view} handleClose={() => handleClose("view")} rowData={selectedRow} />
            </Container>
        </div>
    );
};
